import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Clock, ChartLineUp } from "@phosphor-icons/react";
import Sparkline from "../Sparkline";

/**
 * Admin tile — Grace window engagement
 *
 * Visualises whether the 2-hour grace period (during which ended pins stay
 * visible as muted "Ended" markers) is doing real engagement work. Shows:
 *   • total `pin_click` events in the window (clicks on any truck)
 *   • subset that hit ended pins (`pin_click_ended`)
 *   • % share + 7-day sparkline of ended-pin clicks
 *
 * Decision rule baked into the copy:
 *   - high share (>= 8%) → grace earning its keep, consider extending
 *   - medium (3–8%) → keep at 2h
 *   - low (< 3%) → consider shrinking to 1h to declutter the map
 */
export default function GraceEngagementCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api
      .get("/admin/grace-engagement", { params: { days: 7 } })
      .then(({ data }) => mounted && setData(data))
      .catch(() => mounted && setData(null))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const share = data?.ended_share_pct ?? 0;
  const verdict =
    share >= 8
      ? { tone: "earning", label: "Earning its keep", color: "text-emerald-600 dark:text-emerald-400", hint: "High share — consider extending the window." }
      : share >= 3
      ? { tone: "neutral", label: "Doing its job", color: "text-foreground", hint: "Healthy engagement — keep at 2h." }
      : { tone: "low", label: "Underused", color: "text-muted-foreground", hint: "Low share — could shrink to 1h to declutter." };

  const series = (data?.series || []).map((p) => p.count);

  return (
    <section className="rounded-2xl border border-border bg-card p-5" data-testid="admin-grace-engagement">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">
            <Clock weight="duotone" className="h-4 w-4 text-primary" />
            Grace window engagement
          </div>
          <h2 className="font-display font-semibold text-lg mt-1">
            2-hour ended-pin grace · last 7 days
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            Customer clicks on already-ended trucks (visible only because of the grace window).
          </p>
        </div>
        {!loading && data && (
          <div className={`text-xs font-semibold ${verdict.color}`} data-testid="admin-grace-verdict">
            {verdict.label}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
        <div className="rounded-xl border border-border bg-background/50 p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">All pin clicks</div>
          <div className="mt-1 font-display font-bold text-2xl tabular-nums" data-testid="grace-total">
            {loading ? "…" : (data?.pin_clicks_total ?? 0)}
          </div>
        </div>
        <div className="rounded-xl border border-border bg-background/50 p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Ended-pin clicks</div>
          <div className="mt-1 font-display font-bold text-2xl tabular-nums" data-testid="grace-ended">
            {loading ? "…" : (data?.pin_clicks_ended ?? 0)}
          </div>
        </div>
        <div className="rounded-xl border border-border bg-background/50 p-3">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
            <ChartLineUp weight="duotone" className="h-3 w-3" /> Share
          </div>
          <div className="mt-1 font-display font-bold text-2xl tabular-nums" data-testid="grace-share">
            {loading ? "…" : `${share}%`}
          </div>
          {!loading && series.length > 1 && (
            <div className="mt-1 h-6">
              <Sparkline values={series} color="hsl(var(--primary))" />
            </div>
          )}
        </div>
      </div>

      {!loading && data && (
        <p className="text-xs text-muted-foreground mt-3" data-testid="admin-grace-hint">
          {verdict.hint}
        </p>
      )}
    </section>
  );
}
